"use client"

import { t } from "@/languages/i18n"
/**
 * Error summary card (Lane D).
 *
 * Renders above the form's main column when validation fails. Each
 * error is a button that scrolls the target field into view, focuses
 * it, and pulses a danger-color ring.
 *
 * Caller is responsible for providing field IDs that match the actual
 * DOM ids the antd Form.Item generated. Convention: `name="title"` →
 * id attribute `${formName}_title` (antd default).
 */

import { CloseOutlined, WarningFilled } from "@ant-design/icons"
import { Button } from "antd"
import { useEffect, useRef, useState } from "react"

export interface ErrorSummaryItem {
  /** Antd Form.Item name path. Used to build the DOM id. */
  fieldName: string
  /** Human-readable error message in VN. */
  message: string
}

interface ErrorSummaryProps {
  errors: ErrorSummaryItem[]
  /**
   * The antd Form's `name` prop. Antd generates ids as
   *   `${formName}_${fieldName}` for top-level fields.
   * Pass empty string if your form has no name (then id is just `${fieldName}`).
   */
  formName?: string
  onDismiss?: () => void
  /**
   * Extra hook for nested fields (Form.List items, etc.) where the DOM
   * id can't be derived. Caller can override the default id calculator.
   */
  resolveFieldId?: (fieldName: string) => string
}

const pulseRing = (el: HTMLElement) => {
  el.style.boxShadow = "0 0 0 2px var(--admin-danger)"
  el.style.transition = "box-shadow 600ms ease-out"
  setTimeout(() => {
    el.style.boxShadow = ""
  }, 600)
}

export function ErrorSummary({
  errors,
  formName = "",
  onDismiss,
  resolveFieldId,
}: ErrorSummaryProps) {
  const [dismissed, setDismissed] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Auto-scroll the summary itself into view when errors arrive.
  useEffect(() => {
    if (errors.length > 0) {
      setDismissed(false)
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }, [errors])

  if (errors.length === 0 || dismissed) return null

  const handleJump = (fieldName: string) => {
    const id = resolveFieldId
      ? resolveFieldId(fieldName)
      : formName
        ? `${formName}_${fieldName}`
        : fieldName
    const el = document.getElementById(id)
    if (!el) return
    el.scrollIntoView({ behavior: "smooth", block: "center" })
    // Focus after scroll settles. inputs accept focus directly; for
    // wrappers (e.g. Select) we look for the inner input.
    setTimeout(() => {
      const focusable =
        el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT"
          ? el
          : el.querySelector<HTMLElement>("input, textarea, select, [tabindex]")
      focusable?.focus()
      pulseRing(el)
    }, 350)
  }

  return (
    <div
      ref={ref}
      role="alert"
      aria-live="assertive"
      style={{
        background: "color-mix(in oklch, var(--admin-danger) 12%, transparent)",
        borderLeft: "3px solid var(--admin-danger)",
        borderRadius: 6,
        padding: "16px 20px",
        position: "relative",
      }}
    >
      <div className="flex items-start gap-3">
        <WarningFilled
          aria-hidden
          style={{ color: "var(--admin-danger)", fontSize: 18, marginTop: 2 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              color: "var(--admin-danger)",
              fontSize: 14,
              marginBottom: 8,
            }}
          >
            {t("Có")} {errors.length} {t("lỗi cần sửa trước khi lưu")}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {errors.map((err, i) => (
              <li key={`${err.fieldName}-${i}`} style={{ marginBottom: 4 }}>
                <button
                  type="button"
                  onClick={() => handleJump(err.fieldName)}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    color: "var(--admin-foreground)",
                    fontSize: 13,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  • <span style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
                    {err.message}
                  </span>{" "}
                  <span style={{ color: "var(--admin-muted-fg)", fontSize: 12 }}>
                    → {t("Nhảy tới trường")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        {onDismiss !== undefined && (
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label="Đóng cảnh báo"
            onClick={() => {
              setDismissed(true)
              onDismiss?.()
            }}
          />
        )}
      </div>
    </div>
  )
}

export default ErrorSummary
